import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from './Header';
import Footer from './Footer';
import { API_URL, getImageUrl, normalizeList } from '../config';
import './AdminKycPage.css';

const getApiError = (err, fallback) => {
  const data = err?.response?.data;
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  return data.message || data.detail || data.title || fallback;
};

const pick = (obj, ...keys) => {
  if (!obj) return undefined;
  for (const key of keys) {
    if (obj[key] != null && obj[key] !== '') return obj[key];
  }
  return undefined;
};

const unwrapRecord = (data) => {
  if (!data || typeof data !== 'object') return {};
  if (data.$values && !pick(data, 'kycDocumentId', 'KycDocumentId', 'userId', 'UserId')) {
    const first = normalizeList(data)[0];
    return first && typeof first === 'object' ? first : {};
  }
  return data;
};

const getKycId = (item) => Number(pick(item, 'kycDocumentId', 'KycDocumentId') || 0);
const getBankId = (item) => Number(pick(item, 'bankDetailId', 'BankDetailId') || 0);
const isPendingStatus = (status) => String(status || '').toLowerCase() === 'pending';

const AdminKycPage = () => {
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const userRole = useSelector((state) => state.auth.userRole);
  const token = localStorage.getItem('jwtToken');
  const authHeaders = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  const [kycList, setKycList] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedKyc, setSelectedKyc] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [filter, setFilter] = useState('Pending');
  const [kycMode, setKycMode] = useState('identity');
  const [bankList, setBankList] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const fetchKycList = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/Kyc/all?status=${filter}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Handle both direct array and $values wrapped response
      setKycList(normalizeList(response.data).map((item) => unwrapRecord(item)));
    } catch (err) {
      console.error('Failed to load KYC list:', err);
      setKycList([]);
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/Kyc/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (err) {
      console.error('Failed to load KYC stats:', err);
    }
  }, [token]);

  const fetchBankPending = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/Kyc/bank-pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBankList(normalizeList(response.data).map((item) => unwrapRecord(item)));
    } catch (err) {
      console.error('Failed to load bank KYC list:', err);
      setBankList([]);
    }
  }, [token]);

  useEffect(() => {
    if (!isLoggedIn || userRole !== 'Admin') {
      navigate('/');
      return;
    }
    fetchKycList();
    fetchStats();
    fetchBankPending();
  }, [isLoggedIn, userRole, navigate, fetchKycList, fetchStats, fetchBankPending]);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const viewBankDetails = async (bank) => {
    const bankId = getBankId(bank);
    setSelectedBank({
      ...bank,
      bankDetailId: bankId || bank?.bankDetailId,
    });
    if (!bankId) return;

    try {
      const response = await axios.get(`${API_URL}/Kyc/bank-detail/${bankId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const detail = unwrapRecord(response.data);
      setSelectedBank({
        ...bank,
        ...detail,
        bankDetailId: getBankId(detail) || bankId,
      });
    } catch (err) {
      console.error('Failed to load bank KYC detail:', err);
    }
  };

  const renderPassbookProof = (url) => {
    const proofUrl = url;
    return (
      <div className="doc-image" style={{ marginTop: 12 }}>
        <label>Passbook Proof</label>
        {proofUrl ? (
          <a href={getImageUrl(proofUrl)} target="_blank" rel="noopener noreferrer">
            <img
              src={getImageUrl(proofUrl)}
              alt="Passbook proof"
              onError={(e) => {
                e.target.style.display = 'none';
                if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="image-placeholder" style={{ display: 'none' }}>
              <span>Click to view passbook photo</span>
            </div>
          </a>
        ) : (
          <div className="image-placeholder">
            <span>No passbook photo uploaded</span>
          </div>
        )}
      </div>
    );
  };

  const viewKycDetails = async (kyc) => {
    const kycId = getKycId(kyc);
    setSelectedKyc({
      ...kyc,
      kycDocumentId: kycId || kyc?.kycDocumentId,
    });
    if (!kycId) {
      showMessage('error', 'This KYC request is missing an id.');
      return;
    }

    setDetailLoading(true);
    try {
      let response;
      try {
        response = await axios.get(`${API_URL}/Kyc/${kycId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (firstErr) {
        if (firstErr?.response?.status === 404) {
          response = await axios.get(`${API_URL}/Kyc/detail/${kycId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } else {
          throw firstErr;
        }
      }
      const detail = unwrapRecord(response.data);
      const merged = {
        ...kyc,
        ...detail,
        kycDocumentId: getKycId(detail) || kycId,
      };
      if (merged.user) {
        merged.user = {
          ...merged.user,
          bankDetails: normalizeList(merged.user.bankDetails || merged.bankDetails),
        };
      }
      merged.bankDetails = normalizeList(merged.bankDetails || merged.user?.bankDetails);
      setSelectedKyc(merged);
    } catch (err) {
      showMessage('error', getApiError(err, 'Failed to load full KYC details. Showing submitted summary.'));
    } finally {
      setDetailLoading(false);
    }
  };

  const handleApprove = async (kycId) => {
    if (!window.confirm('Are you sure you want to approve this KYC?')) return;
    
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/Kyc/${kycId}/approve`, {}, { headers: authHeaders });
      showMessage('success', 'Identity KYC approved successfully');
      setSelectedKyc(null);
      fetchKycList();
      fetchStats();
      fetchBankPending();
    } catch (err) {
      showMessage('error', getApiError(err, 'Failed to approve identity KYC'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      showMessage('error', 'Please provide a rejection reason');
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(
        `${API_URL}/Kyc/${getKycId(selectedKyc)}/reject`,
        { rejectionReason: rejectReason },
        { headers: authHeaders }
      );
      showMessage('success', 'Identity KYC rejected');
      setShowRejectModal(false);
      setRejectReason('');
      setSelectedKyc(null);
      fetchKycList();
      fetchStats();
      fetchBankPending();
    } catch (err) {
      showMessage('error', getApiError(err, 'Failed to reject identity KYC'));
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const classes = {
      'Pending': 'badge-warning',
      'Approved': 'badge-success',
      'Rejected': 'badge-danger'
    };
    return classes[status] || classes[String(status || '').replace(/^\w/, (c) => c.toUpperCase())] || 'badge-default';
  };

  const handleApproveBank = async () => {
    const bankDetailId = selectedBank?.bankDetailId || selectedBank?.BankDetailId;
    const userId = selectedBank?.userId || selectedBank?.UserId;
    if (!bankDetailId && !userId) {
      showMessage('error', 'Missing bank record id');
      return;
    }
    if (!window.confirm('Approve this bank KYC?')) return;

    setActionLoading(true);
    try {
      if (bankDetailId) {
        await axios.post(`${API_URL}/Kyc/bank-detail/${bankDetailId}/approve`, {}, { headers: authHeaders });
      } else {
        await axios.post(`${API_URL}/Kyc/bank/${userId}/approve`, {}, { headers: authHeaders });
      }
      showMessage('success', 'Bank KYC approved successfully');
      setSelectedBank(null);
      fetchBankPending();
    } catch (err) {
      showMessage('error', getApiError(err, 'Failed to approve bank KYC'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectBank = async () => {
    if (!rejectReason.trim()) {
      showMessage('error', 'Please provide a rejection reason');
      return;
    }
    const bankDetailId = selectedBank?.bankDetailId || selectedBank?.BankDetailId;
    if (!bankDetailId) {
      showMessage('error', 'Missing bank record id');
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(
        `${API_URL}/Kyc/bank-detail/${bankDetailId}/reject`,
        { rejectionReason: rejectReason },
        { headers: authHeaders }
      );
      showMessage('success', 'Bank KYC rejected');
      setShowRejectModal(false);
      setRejectReason('');
      setSelectedBank(null);
      fetchBankPending();
    } catch (err) {
      showMessage('error', getApiError(err, 'Failed to reject bank KYC'));
    } finally {
      setActionLoading(false);
    }
  };

  if (!isLoggedIn || userRole !== 'Admin') {
    return null;
  }

  return (
    <div className="admin-kyc-page">
      <Header />
      <div className="admin-kyc-container">
        <div className="page-header">
          <h1>KYC Verification Management</h1>
          <p>Review identity KYC and bank KYC for customers and sellers</p>
        </div>

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        {/* Stats Cards */}
        <div className="stats-cards">
          <div className="stat-card pending" onClick={() => setFilter('Pending')}>
            <div className="stat-number">{stats.pending}</div>
            <div className="stat-label">Pending Review</div>
          </div>
          <div className="stat-card approved" onClick={() => setFilter('Approved')}>
            <div className="stat-number">{stats.approved}</div>
            <div className="stat-label">Approved</div>
          </div>
          <div className="stat-card rejected" onClick={() => setFilter('Rejected')}>
            <div className="stat-number">{stats.rejected}</div>
            <div className="stat-label">Rejected</div>
          </div>
          <div className="stat-card total" onClick={() => setFilter('')}>
            <div className="stat-number">{stats.total}</div>
            <div className="stat-label">Total</div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="filter-tabs">
          <button
            className={`filter-tab ${kycMode === 'identity' ? 'active' : ''}`}
            onClick={() => { setKycMode('identity'); setSelectedBank(null); }}
          >
            Identity KYC
          </button>
          <button
            className={`filter-tab ${kycMode === 'bank' ? 'active' : ''}`}
            onClick={() => { setKycMode('bank'); setSelectedKyc(null); }}
          >
            Bank KYC ({bankList.length})
          </button>
        </div>

        {kycMode === 'identity' && (
        <>
        <div className="filter-tabs">
          <button 
            className={`filter-tab ${filter === 'Pending' ? 'active' : ''}`}
            onClick={() => setFilter('Pending')}
          >
            Pending
          </button>
          <button 
            className={`filter-tab ${filter === 'Approved' ? 'active' : ''}`}
            onClick={() => setFilter('Approved')}
          >
            Approved
          </button>
          <button 
            className={`filter-tab ${filter === 'Rejected' ? 'active' : ''}`}
            onClick={() => setFilter('Rejected')}
          >
            Rejected
          </button>
          <button 
            className={`filter-tab ${filter === '' ? 'active' : ''}`}
            onClick={() => setFilter('')}
          >
            All
          </button>
        </div>

        <div className="kyc-content">
          {/* KYC List */}
          <div className="kyc-list-panel">
            {loading ? (
              <div className="loading">Loading KYC requests...</div>
            ) : kycList.length === 0 ? (
              <div className="empty-state">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <polyline points="10 9 9 9 8 9"/>
                </svg>
                <p>No KYC requests found</p>
              </div>
            ) : (
              <div className="kyc-list">
                {kycList.map((kyc) => {
                  const kycId = getKycId(kyc);
                  return (
                  <div 
                    key={kycId || kyc.email || kyc.username}
                    className={`kyc-list-item ${getKycId(selectedKyc) === kycId && kycId ? 'selected' : ''}`}
                    onClick={() => viewKycDetails(kyc)}
                  >
                    <div className="kyc-item-header">
                      <span className="username">{pick(kyc, 'fullName', 'FullName', 'username', 'Username')}</span>
                      <span className={`badge ${getStatusBadge(pick(kyc, 'status', 'Status'))}`}>{pick(kyc, 'status', 'Status')}</span>
                    </div>
                    <div className="kyc-item-info">
                      <p><strong>Role:</strong> {pick(kyc, 'roleName', 'RoleName') || 'Customer'}</p>
                      <p><strong>Document:</strong> {pick(kyc, 'documentType', 'DocumentType')}</p>
                      <p><strong>Submitted:</strong> {new Date(pick(kyc, 'submittedAt', 'SubmittedAt')).toLocaleDateString()}</p>
                      <p><strong>Email:</strong> {pick(kyc, 'email', 'Email')}</p>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* KYC Detail Panel */}
          <div className="kyc-detail-panel">
            {selectedKyc ? (
              <div className="kyc-detail">
                <div className="detail-header">
                  <h2>KYC Details{detailLoading ? ' (updating...)' : ''}</h2>
                  <span className={`badge ${getStatusBadge(pick(selectedKyc, 'status', 'Status'))}`}>
                    {pick(selectedKyc, 'status', 'Status')}
                  </span>
                </div>

                {/* User Info */}
                <div className="detail-section">
                  <h3>User Information</h3>
                  <div className="info-grid">
                    <div className="info-item">
                      <label>Role</label>
                      <span>{selectedKyc.user?.roleName || selectedKyc.roleName || 'Customer'}</span>
                    </div>
                    <div className="info-item">
                      <label>Username</label>
                      <span>{selectedKyc.user?.username || selectedKyc.username || '—'}</span>
                    </div>
                    <div className="info-item">
                      <label>Full Name</label>
                      <span>{selectedKyc.user?.fullName || selectedKyc.user?.ownerName || selectedKyc.fullName || 'Not provided'}</span>
                    </div>
                    <div className="info-item">
                      <label>Email</label>
                      <span>{selectedKyc.user?.email || selectedKyc.email || '—'}</span>
                    </div>
                    <div className="info-item">
                      <label>Phone</label>
                      <span>{selectedKyc.user?.phoneNumber || 'Not provided'}</span>
                    </div>
                    <div className="info-item full-width">
                      <label>Address</label>
                      <span>
                        {selectedKyc.user?.address ?
                          `${selectedKyc.user.address}, ${selectedKyc.user.city || ''} ${selectedKyc.user.state || ''} ${selectedKyc.user.pinCode || ''}`
                          : 'Not provided'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Document Info */}
                <div className="detail-section">
                  <h3>Document Information</h3>
                  <div className="info-grid">
                    <div className="info-item">
                      <label>Document Type</label>
                      <span>{selectedKyc.documentType}</span>
                    </div>
                    <div className="info-item">
                      <label>Document Number</label>
                      <span>{selectedKyc.documentNumber}</span>
                    </div>
                    <div className="info-item">
                      <label>Submitted On</label>
                      <span>{new Date(selectedKyc.submittedAt).toLocaleString()}</span>
                    </div>
                    {selectedKyc.reviewedAt && (
                      <div className="info-item">
                        <label>Reviewed On</label>
                        <span>{new Date(selectedKyc.reviewedAt).toLocaleString()}</span>
                      </div>
                    )}
                    {selectedKyc.reviewedBy && (
                      <div className="info-item">
                        <label>Reviewed By</label>
                        <span>{selectedKyc.reviewedBy}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Document Images */}
                <div className="detail-section">
                  <h3>Document Images</h3>
                  <div className="document-images">
                    <div className="doc-image">
                      <label>Front Side</label>
                      <a href={getImageUrl(selectedKyc.documentFrontUrl)} target="_blank" rel="noopener noreferrer">
                        <img
                          src={getImageUrl(selectedKyc.documentFrontUrl)}
                          alt="Document Front"
                          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                        />
                        <div className="image-placeholder" style={{ display: 'none' }}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/>
                          </svg>
                          <span>Click to view</span>
                        </div>
                      </a>
                    </div>
                    {selectedKyc.documentBackUrl && (
                      <div className="doc-image">
                        <label>Back Side</label>
                        <a href={getImageUrl(selectedKyc.documentBackUrl)} target="_blank" rel="noopener noreferrer">
                          <img
                            src={getImageUrl(selectedKyc.documentBackUrl)}
                            alt="Document Back"
                            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                          />
                          <div className="image-placeholder" style={{ display: 'none' }}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                              <polyline points="14 2 14 8 20 8"/>
                            </svg>
                            <span>Click to view</span>
                          </div>
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {/* Bank Details — always visible on KYC approval */}
                {(() => {
                  const banks = normalizeList(selectedKyc.user?.bankDetails || selectedKyc.bankDetails);
                  if (banks.length === 0) {
                    return (
                      <div className="detail-section">
                        <h3>Bank Details</h3>
                        <p>No bank KYC submitted for this {selectedKyc.user?.roleName === 'Seller' ? 'seller' : 'user'}.</p>
                      </div>
                    );
                  }
                  return (
                  <div className="detail-section">
                    <h3>Bank Details</h3>
                    {banks.map((bank, index) => (
                      <React.Fragment key={bank.bankDetailId || index}>
                      <div className="info-grid">
                        <div className="info-item">
                          <label>Account Holder</label>
                          <span>{bank.accountHolderName}</span>
                        </div>
                        <div className="info-item">
                          <label>Account Number</label>
                          <span>{bank.accountNumber}</span>
                        </div>
                        <div className="info-item">
                          <label>Bank Name</label>
                          <span>{bank.bankName}</span>
                        </div>
                        <div className="info-item">
                          <label>Branch</label>
                          <span>{bank.branchName}{bank.branchState ? `, ${bank.branchState}` : ''}</span>
                        </div>
                        <div className="info-item">
                          <label>IFSC Code</label>
                          <span>{bank.ifscCode || '—'}</span>
                        </div>
                        <div className="info-item">
                          <label>Account Type</label>
                          <span>{bank.accountType}</span>
                        </div>
                        <div className="info-item">
                          <label>Bank KYC</label>
                          <span>{bank.isVerified ? 'Verified' : 'Pending'}</span>
                        </div>
                      </div>
                      {renderPassbookProof(pick(bank, 'passbookProofUrl', 'PassbookProofUrl'))}
                      </React.Fragment>
                    ))}
                  </div>
                  );
                })()}

                {/* Rejection Reason */}
                {selectedKyc.rejectionReason && (
                  <div className="detail-section rejection">
                    <h3>Rejection Reason</h3>
                    <p>{selectedKyc.rejectionReason}</p>
                  </div>
                )}

                {/* Action Buttons */}
                {isPendingStatus(pick(selectedKyc, 'status', 'Status')) && (
                  <div className="action-buttons">
                    <button 
                      className="btn-reject"
                      onClick={() => setShowRejectModal(true)}
                      disabled={actionLoading}
                    >
                      Reject
                    </button>
                    <button 
                      className="btn-approve"
                      onClick={() => handleApprove(getKycId(selectedKyc))}
                      disabled={actionLoading}
                    >
                      {actionLoading ? 'Processing...' : 'Approve'}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="empty-detail">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <p>Select a KYC request to view details</p>
              </div>
            )}
          </div>
        </div>
        </>
        )}

        {kycMode === 'bank' && (
        <div className="kyc-content">
          <div className="kyc-list-panel">
            {bankList.length === 0 ? (
              <div className="empty-state">
                <p>No pending bank KYC requests</p>
              </div>
            ) : (
              <div className="kyc-list">
                {bankList.map((bank) => {
                  const bankId = getBankId(bank);
                  return (
                  <div
                    key={bankId || bank.email || bank.username}
                    className={`kyc-list-item ${getBankId(selectedBank) === bankId && bankId ? 'selected' : ''}`}
                    onClick={() => viewBankDetails(bank)}
                  >
                    <div className="kyc-item-header">
                      <span className="username">{pick(bank, 'ownerName', 'OwnerName', 'fullName', 'FullName', 'username', 'Username')}</span>
                      <span className="badge badge-warning">Pending</span>
                    </div>
                    <div className="kyc-item-info">
                      <p><strong>Role:</strong> {pick(bank, 'roleName', 'RoleName') || '—'}</p>
                      <p><strong>Bank:</strong> {pick(bank, 'bankName', 'BankName')}</p>
                      <p><strong>Email:</strong> {pick(bank, 'email', 'Email')}</p>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="kyc-detail-panel">
            {selectedBank ? (
              <div className="kyc-detail">
                <div className="detail-header">
                  <h2>Bank KYC Details</h2>
                  <span className="badge badge-warning">Pending</span>
                </div>
                <div className="detail-section">
                  <h3>User Information</h3>
                  <div className="info-grid">
                    <div className="info-item">
                      <label>Role</label>
                      <span>{pick(selectedBank, 'roleName', 'RoleName') || '—'}</span>
                    </div>
                    <div className="info-item">
                      <label>Name</label>
                      <span>{pick(selectedBank, 'ownerName', 'OwnerName', 'fullName', 'FullName', 'username', 'Username')}</span>
                    </div>
                    <div className="info-item">
                      <label>Email</label>
                      <span>{pick(selectedBank, 'email', 'Email')}</span>
                    </div>
                  </div>
                </div>
                <div className="detail-section">
                  <h3>Bank Details</h3>
                  <div className="info-grid">
                    <div className="info-item">
                      <label>Account Holder</label>
                      <span>{pick(selectedBank, 'accountHolderName', 'AccountHolderName')}</span>
                    </div>
                    <div className="info-item">
                      <label>Account Number</label>
                      <span>{pick(selectedBank, 'accountNumber', 'AccountNumber')}</span>
                    </div>
                    <div className="info-item">
                      <label>Bank Name</label>
                      <span>{pick(selectedBank, 'bankName', 'BankName')}</span>
                    </div>
                    <div className="info-item">
                      <label>Branch</label>
                      <span>{pick(selectedBank, 'branchName', 'BranchName')}{pick(selectedBank, 'branchState', 'BranchState') ? `, ${pick(selectedBank, 'branchState', 'BranchState')}` : ''}</span>
                    </div>
                    <div className="info-item">
                      <label>IFSC</label>
                      <span>{pick(selectedBank, 'ifscCode', 'IFSCCode') || '—'}</span>
                    </div>
                  </div>
                  {renderPassbookProof(pick(selectedBank, 'passbookProofUrl', 'PassbookProofUrl'))}
                </div>
                <div className="action-buttons">
                  <button
                    className="btn-reject"
                    onClick={() => setShowRejectModal(true)}
                    disabled={actionLoading}
                  >
                    Reject
                  </button>
                  <button
                    className="btn-approve"
                    onClick={handleApproveBank}
                    disabled={actionLoading}
                  >
                    {actionLoading ? 'Processing...' : 'Approve Bank KYC'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="empty-detail">
                <p>Select a bank KYC request to view details</p>
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{kycMode === 'bank' ? 'Reject Bank KYC' : 'Reject Identity KYC'}</h3>
            <p>Please provide a reason for rejection. This will be shown to the user.</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter rejection reason..."
              rows="4"
            />
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowRejectModal(false)}>
                Cancel
              </button>
              <button
                className="btn-reject"
                onClick={kycMode === 'bank' ? handleRejectBank : handleReject}
                disabled={actionLoading}
              >
                {actionLoading ? 'Rejecting...' : 'Confirm Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default AdminKycPage;
